import { computed, makeObservable, observable } from 'mobx';
import moment from 'moment';

import { ModelArrayCast, ModelCast } from 'shared/casts';
import { ICONS, ROUTES } from 'shared/constants';
import { NotificationTypeEnum } from 'shared/enums';

import {
    AnnouncementModel,
    ArticleModel,
    BookModel,
    CommentModel,
    DocumentModel,
    EducationLessonModel, EssentialModel,
    GalleryModel,
    NewsModel,
    SurveyModel,
    UserModel,
    VacancyModel,
} from 'shared/models';

import { TaskModel } from 'shared/models/TaskTracker';

import { Model } from './Model';

interface INotificationDataModel {
    count?: number;
    users?: UserModel[];
    user?: UserModel;
    document?: DocumentModel;
    comment?: CommentModel;
    task?: TaskModel;
    book?: BookModel;
    announcement?: AnnouncementModel;
    essential?: EssentialModel;
    vacancy?: VacancyModel;
    article?: ArticleModel;
    educationLesson?: EducationLessonModel;
    survey?: SurveyModel;
    gallery?: GalleryModel;
    news?: NewsModel;
}

class NotificationDataModel extends Model implements INotificationDataModel {
    casts = {
        users: new ModelArrayCast(UserModel),
        user: new ModelCast(UserModel),
        document: new ModelCast(DocumentModel),
        comment: new ModelCast(CommentModel),
        task: new ModelCast(TaskModel),
        book: new ModelCast(BookModel),
        announcement: new ModelCast(AnnouncementModel),
        vacancy: new ModelCast(VacancyModel),
        article: new ModelCast(ArticleModel),
        educationLesson: new ModelCast(EducationLessonModel),
        survey: new ModelCast(SurveyModel),
        gallery: new ModelCast(GalleryModel),
        news: new ModelCast(NewsModel),
        essential: new ModelCast(EssentialModel),
    };

    count: number = 0;
    url: string = '';
    users: UserModel[] = [];
    user: UserModel = new UserModel();
    task: TaskModel = new TaskModel();
    document: DocumentModel = new DocumentModel();
    comment: CommentModel = new CommentModel();
    book: BookModel = new BookModel();
    announcement: AnnouncementModel = new AnnouncementModel();
    vacancy: VacancyModel = new VacancyModel();
    article: ArticleModel = new ArticleModel();
    educationLesson: EducationLessonModel = new EducationLessonModel();
    survey: SurveyModel = new SurveyModel();
    gallery: GalleryModel = new GalleryModel();
    news: NewsModel = new NewsModel();
    essential = new EssentialModel();

    constructor(payload: INotificationDataModel = {}) {
        super();

        makeObservable(this, {
            users: observable,
            user: observable,
            task: observable,
            count: observable,
            document: observable,
            comment: observable,
            book: observable,
            announcement: observable,
            vacancy: observable,
            article: observable,
            educationLesson: observable,
            survey: observable,
            gallery: observable,
            news: observable,
            essential: observable,
        });

        this.update(payload);
    }
}

export interface INotificationModel {
    id?: string;
    createdAt?: string;
    typeId?: string;
    readAt?: string | null;
    deletedAt?: string | null;
    data?: NotificationDataModel;
}

export class NotificationModel extends Model implements INotificationModel {
    casts = {
        data: new ModelCast(NotificationDataModel),
    };

    id = '';
    createdAt = '';
    readAt = null;
    deletedAt = null;
    typeId = '';
    data = new NotificationDataModel();

    constructor(payload: INotificationModel = {}) {
        super();

        makeObservable(this, {
            id: observable,
            createdAt: observable,
            readAt: observable,
            deletedAt: observable,
            typeId: observable,
            data: observable,
            typeFormatted: computed,
            type: computed,
            isUnread: computed,
            isDeleted: computed,
            createdAtMoment: computed,
            content: computed,
        });

        this.update(payload);
    }

    get content(): { name: string; action: string; description: string; url: string; icon: typeof ICONS.NEWS } {
        switch (this.typeFormatted) {
            case 'UnreadChatMessagesNotification':
                return {
                    name:
                        this.data.count === 1
                            ? `У вас 1 непрочитанное сообщение!`
                            : `У вас ${this.data.count} непрочитанных сообщения!`,
                    action: 'Ответить',
                    description: '',
                    url: ROUTES.CALENDAR('?calendarItemType=birthday'),
                    icon: ICONS.COMMENT,
                };
            case 'UsersBirthdaysNotification':
                return {
                    name: `День Рождения! (${this.data.users.length})`,
                    action: 'Перейти в календарь',
                    description: '',
                    url: '#',
                    icon: ICONS.NOTIFICATIONS_BIRTHDAY,
                };
            case 'VacancyCreatedNotification':
                return {
                    name: 'Новая вакансия',
                    action: 'Перейти',
                    description: this.data.vacancy.name,
                    url: ROUTES.VACANCY(this.data.vacancy.id),
                    icon: ICONS.VACANCIES,
                };
            case 'SurveyCreatedNotification':
                return {
                    name: 'Новый опрос',
                    action: 'Перейти',
                    description: this.data.survey.name,
                    url: ROUTES.SURVEY(this.data.survey.id),
                    icon: ICONS.SURVEY,
                };
            case 'GalleryCreatedNotification':
                return {
                    name: 'Новый альбом в галерее Togas Life',
                    action: 'Перейти',
                    description: this.data.gallery.name,
                    url: ROUTES.GALLERY(this.data.gallery.id),
                    icon: ICONS.IMAGES_AND_VIDEOS,
                };
        }
        return {
            name: 'Уведомление',
            action: 'Перейти',
            description: '',
            url: '#',
            icon: ICONS.ANNOUNCEMENT,
        };
    }

    get createdAtMoment() {
        if (!this.createdAt) {
            return moment();
        }
        return moment(this.createdAt);
    }

    createdAtFormatted = () => {
        if (!this.createdAt) {
            return '';
        }

        const duration = moment.duration(moment().diff(this.createdAtMoment));
        const hoursFromNow = duration.asHours();

        if (hoursFromNow < 1) {
            return this.createdAtMoment.fromNow();
        }

        if (hoursFromNow < 10) {
            return  this.createdAtMoment.format('HH:mm');
        }

        return this.createdAtMoment.format('D MMMM, HH:mm');
    };

    get typeFormatted() {
        return this.typeId.replace('App\\Notifications\\', '').split('\\').join('');
    }

    get type(): ReturnType<typeof NotificationTypeEnum.from> {
        return NotificationTypeEnum.from(this.typeId.replace('App\\Notifications\\', '').split('\\').join(''));
    }

    get isUnread() {
        return !this.readAt;
    }

    get isDeleted() {
        return !!this.deletedAt;
    }
}
